<template>
  <div class="virtual-viewport-demo">
    <div class="demo-header">
      <h1>VirtualViewport Demo</h1>
      <p>Liste de {{ items.length }} items avec hauteurs dynamiques</p>

      <div class="demo-controls">
        <button @click="scrollToTop">Haut</button>
        <button @click="scrollToMiddle">Milieu</button>
        <button @click="scrollToEnd">Bas</button>
        <button @click="randomizeHeight">Randomiser hauteur index {{ randomIndex }}</button>
        <button @click="refreshData">Regénérer données</button>
      </div>

      <div class="demo-stats">
        <span>Items générés: {{ items.length }}</span>
        <span>Estimation taille: {{ estimateSize }}px</span>
        <span>Overscan: {{ overscan }}</span>
      </div>
    </div>

    <div class="demo-viewport">
      <VirtualViewport
        ref="virtualRef"
        :count="items.length"
        :estimate-size="estimateSize"
        :overscan="overscan"
      >
        <template #default="{ index }">
          <div class="demo-item" :style="{ backgroundColor: items[index].color }">
            <div class="item-header">
              <span class="item-index">Item #{{ index }}</span>
              <span class="item-height">{{ items[index].height }}px</span>
            </div>

            <h3 class="item-title">{{ items[index].title }}</h3>

            <p class="item-content">{{ items[index].content }}</p>

            <div class="item-meta">
              <span class="badge">{{ items[index].category }}</span>
              <span class="date">{{ items[index].date }}</span>
            </div>

            <!-- Simule du contenu variable -->
            <div v-if="items[index].hasImage" class="item-image-placeholder">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>

            <div v-if="items[index].tags.length > 0" class="item-tags">
              <span v-for="tag in items[index].tags" :key="tag" class="tag">{{ tag }}</span>
            </div>
          </div>
        </template>
      </VirtualViewport>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
// import VirtualViewport from '@/ui/core/VirtualViewport.global.vue';

/**
 * Génération de données aléatoires pour la démo
 */
const categories = ['Business', 'Technology', 'Design', 'Marketing', 'Finance', 'Health'];
const sampleTitles = [
  'Introduction aux concepts fondamentaux',
  'Analyse approfondie du sujet',
  'Perspectives futures et évolutions',
  'Cas pratique et mise en œuvre',
  'Conclusion et recommandations',
  'Méthodologie et approche',
  'Résultats et observations',
  'Discussion et implications'
];

const sampleContent = [
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
  'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
  'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
  'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
  'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium.',
  'Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit.',
  'Quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.'
];

const tags = ['important', 'urgent', 'info', 'warning', 'success', 'draft', 'review', 'approved'];

/**
 * Génère une couleur pastel aléatoire
 */
function getRandomColor() {
  const colors = [
    '#FEF3C7', // amber
    '#D1FAE5', // emerald
    '#DBEAFE', // blue
    '#E0E7FF', // indigo
    '#FCE7F3', // pink
    '#FED7AA', // orange
    '#E9D5FF', // purple
    '#FECACA', // red
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

/**
 * Génère un contenu aléatoire
 */
function generateRandomContent(index) {
  const height = Math.floor(Math.random() * 200) + 80; // 80px à 280px
  const paragraphCount = Math.floor(Math.random() * 4) + 1;
  const hasImage = Math.random() > 0.7;
  const tagCount = Math.floor(Math.random() * 4);
  const itemTags = [];

  for (let i = 0; i < tagCount; i++) {
    const tag = tags[Math.floor(Math.random() * tags.length)];
    if (!itemTags.includes(tag)) {
      itemTags.push(tag);
    }
  }

  // Génère le contenu en paragraphes
  let content = '';
  for (let i = 0; i < paragraphCount; i++) {
    content += sampleContent[Math.floor(Math.random() * sampleContent.length)] + ' ';
  }

  return {
    id: index,
    height,
    color: getRandomColor(),
    title: `${sampleTitles[Math.floor(Math.random() * sampleTitles.length)]} - ${index}`,
    content: content.trim(),
    category: categories[Math.floor(Math.random() * categories.length)],
    date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR'),
    hasImage,
    tags: itemTags
  };
}

/**
 * State
 */
const items = ref([]);
const virtualRef = ref(null);
const randomIndex = ref(0);
const estimateSize = ref(150);
const overscan = ref(5);

/**
 * Génère les données initiales
 */
function generateData(count = 1000) {
  items.value = [];
  for (let i = 0; i < count; i++) {
    items.value.push(generateRandomContent(i));
  }
  console.log(`🚀 Generated ${count} items`);
}

/**
 * Actions
 */
function scrollToTop() {
  virtualRef.value?.scrollToTop();
}

function scrollToMiddle() {
  virtualRef.value?.scrollToIndex(Math.floor(items.value.length / 2), { align: 'center' });
}

function scrollToEnd() {
  virtualRef.value?.scrollToEnd();
}

function randomizeHeight() {
  randomIndex.value = Math.floor(Math.random() * items.value.length);
  const newHeight = Math.floor(Math.random() * 200) + 80;
  items.value[randomIndex.value].height = newHeight;

  // Force le re-calcul après un court délai pour simuler un chargement async
  setTimeout(() => {
    virtualRef.value?.recalculateSizes();
  }, 100);
}

function refreshData() {
  generateData(1000);
  // Scroll vers le haut après régénération
  setTimeout(() => {
    scrollToTop();
  }, 100);
}

/**
 * Init
 */
onMounted(() => {
  generateData(1000);
  console.log('✅ VirtualViewport Demo mounted');
});
</script>

<style scoped>
.virtual-viewport-demo {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  font-family: system-ui, -apple-system, sans-serif;
}

.demo-header {
  background: white;
  padding: 20px;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
}

.demo-header h1 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #111;
}

.demo-header p {
  margin: 0 0 16px 0;
  color: #666;
  font-size: 14px;
}

.demo-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.demo-controls button {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.demo-controls button:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.demo-controls button:active {
  transform: translateY(1px);
}

.demo-stats {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
}

.demo-stats span {
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
}

.demo-viewport {
  flex: 1;
  overflow: hidden;
  padding: 16px;
}

/* Item styles */
.demo-item {
  margin: 0 8px 8px 8px;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.item-index {
  font-weight: bold;
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-height {
  font-size: 11px;
  color: #999;
  background: white;
  padding: 2px 6px;
  border-radius: 4px;
}

.item-title {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #111;
  line-height: 1.4;
}

.item-content {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #444;
  line-height: 1.6;
}

.item-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  background: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  color: #555;
}

.date {
  font-size: 12px;
  color: #888;
}

.item-image-placeholder {
  width: 100%;
  height: 120px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.item-image-placeholder svg {
  width: 48px;
  height: 48px;
  color: #ccc;
}

.item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  display: inline-block;
  padding: 3px 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #555;
}
</style>
